// Production-schedule data hooks. Plain useEffect + fetch wrapper —
// matches the pipe-takeoff hook style. Each hook returns
// {data, error, loading, refetch} so views can render error banners
// without bespoke plumbing. Mutations are async helpers separate
// from the read hooks, exported for direct invocation from event
// handlers.
import { useCallback, useEffect, useState } from "react";
import { api } from "../../../../lib/api";
import type {
  CapacityResponse,
  CycleStageRateDto,
  CycleStageRateUpsertInputT,
  CycleTimesDto,
  CycleWeldRateDto,
  CycleWeldRateUpsertInputT,
  SpoolCompositionDto,
  SpoolCompositionUpsertInputT,
  WorkCalendarDto,
  WorkHolidayCreateInputT,
  WorkHolidayDto,
  WorkHolidayUpdateInputT,
  WorkScheduleDto,
  WorkScheduleUpdateInputT,
  DrawingDto,
  DrawingSpoolMatchDto,
  JobDto,
  JobListResponse,
  JobMessageDto,
  JobStatusCreateInputT,
  JobStatusDto,
  JobStatusUpdateInputT,
  NominalDistributionResult,
  PipeSizeCreateInputT,
  PipeSizeDto,
  PipeSizeUpdateInputT,
  ResourceLoadResponse,
  SolutionTypeDto,
  SpoolDto,
  SpoolStageDto,
  SpoolStageUpdateInputT,
  StageConfigInputT,
  StageConfigUpdateInputT,
  StageDefaultDto,
  StageDefDto,
  TeamMemberDto,
  WeldTypeCreateInputT,
  WeldTypeDto,
  WeldTypeUpdateInputT,
} from "../../../../../../shared/types/production-schedule";

const BASE = "/api/tools/production-schedule";

export interface FetchState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  refetch: () => Promise<void>;
}

function useResource<T>(path: string): FetchState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api<T>(path));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, error, loading, refetch };
}

export function useJobs(search: string): FetchState<JobListResponse> {
  // search is the serialised URLSearchParams from the FilterBar.
  const path = search ? `${BASE}/jobs?${search}` : `${BASE}/jobs`;
  return useResource<JobListResponse>(path);
}

export function useTeam(): FetchState<{ team: TeamMemberDto[] }> {
  return useResource(`${BASE}/team`);
}

export function useSolutionTypes(): FetchState<{ types: SolutionTypeDto[] }> {
  return useResource(`${BASE}/solution-types`);
}

export function useStageDefaults(): FetchState<{ defaults: StageDefaultDto[] }> {
  return useResource(`${BASE}/stage-defaults`);
}

export async function setStageDefault(
  stageKey: string,
  defaultAssigneeId: number | null,
): Promise<StageDefaultDto> {
  return api<StageDefaultDto>(`${BASE}/stage-defaults/${stageKey}`, {
    method: "PUT",
    json: { defaultAssigneeId },
  });
}

export function useStages(): FetchState<{ stages: StageDefDto[] }> {
  return useResource(`${BASE}/stages`);
}

export async function createStageConfig(input: StageConfigInputT): Promise<StageDefDto> {
  return api<StageDefDto>(`${BASE}/stages`, { method: "POST", json: input });
}

export async function updateStageConfig(
  stageKey: string,
  patch: StageConfigUpdateInputT,
): Promise<StageDefDto> {
  return api<StageDefDto>(`${BASE}/stages/${stageKey}`, { method: "PATCH", json: patch });
}

export async function deleteStageConfig(stageKey: string): Promise<{ status: string }> {
  return api(`${BASE}/stages/${stageKey}`, { method: "DELETE" });
}

export async function reorderStageConfigs(keys: string[]): Promise<{ stages: StageDefDto[] }> {
  return api<{ stages: StageDefDto[] }>(`${BASE}/stages/reorder`, {
    method: "PATCH",
    json: { keys },
  });
}

export function useJobStatuses(): FetchState<{ statuses: JobStatusDto[] }> {
  return useResource(`${BASE}/statuses`);
}

export async function createJobStatus(input: JobStatusCreateInputT): Promise<JobStatusDto> {
  return api<JobStatusDto>(`${BASE}/statuses`, { method: "POST", json: input });
}

export async function updateJobStatus(
  statusKey: string,
  patch: JobStatusUpdateInputT,
): Promise<JobStatusDto> {
  return api<JobStatusDto>(`${BASE}/statuses/${statusKey}`, { method: "PATCH", json: patch });
}

export async function archiveJobStatus(statusKey: string): Promise<{ status: string }> {
  return api(`${BASE}/statuses/${statusKey}`, { method: "DELETE" });
}

export async function reorderJobStatuses(keys: string[]): Promise<{ statuses: JobStatusDto[] }> {
  return api<{ statuses: JobStatusDto[] }>(`${BASE}/statuses/reorder`, {
    method: "PATCH",
    json: { keys },
  });
}

// ── Cycle-times catalogue ────────────────────────────────────────
export function useCycleTimes(): FetchState<CycleTimesDto> {
  return useResource(`${BASE}/cycle-times`);
}

export async function createPipeSize(input: PipeSizeCreateInputT): Promise<PipeSizeDto> {
  return api<PipeSizeDto>(`${BASE}/cycle-times/pipe-sizes`, { method: "POST", json: input });
}

export async function updatePipeSize(id: number, patch: PipeSizeUpdateInputT): Promise<PipeSizeDto> {
  return api<PipeSizeDto>(`${BASE}/cycle-times/pipe-sizes/${id}`, { method: "PATCH", json: patch });
}

export async function reorderPipeSizes(ids: number[]): Promise<CycleTimesDto> {
  return api<CycleTimesDto>(`${BASE}/cycle-times/pipe-sizes/reorder`, {
    method: "PATCH",
    json: { ids },
  });
}

export async function createWeldType(input: WeldTypeCreateInputT): Promise<WeldTypeDto> {
  return api<WeldTypeDto>(`${BASE}/cycle-times/weld-types`, { method: "POST", json: input });
}

export async function updateWeldType(id: number, patch: WeldTypeUpdateInputT): Promise<WeldTypeDto> {
  return api<WeldTypeDto>(`${BASE}/cycle-times/weld-types/${id}`, { method: "PATCH", json: patch });
}

export async function reorderWeldTypes(ids: number[]): Promise<CycleTimesDto> {
  return api<CycleTimesDto>(`${BASE}/cycle-times/weld-types/reorder`, {
    method: "PATCH",
    json: { ids },
  });
}

export async function upsertStageRate(
  input: CycleStageRateUpsertInputT,
): Promise<CycleStageRateDto> {
  return api<CycleStageRateDto>(`${BASE}/cycle-times/stage-rates`, { method: "PUT", json: input });
}

export async function deleteStageRate(
  stageKey: string,
  pipeSizeId: number | null,
): Promise<void> {
  const size = pipeSizeId === null ? "null" : String(pipeSizeId);
  await api(`${BASE}/cycle-times/stage-rates?stageKey=${encodeURIComponent(stageKey)}&pipeSizeId=${size}`, {
    method: "DELETE",
  });
}

export async function upsertWeldRate(input: CycleWeldRateUpsertInputT): Promise<CycleWeldRateDto> {
  return api<CycleWeldRateDto>(`${BASE}/cycle-times/weld-rates`, { method: "PUT", json: input });
}

export async function deleteWeldRate(weldTypeId: number, pipeSizeId: number): Promise<void> {
  await api(`${BASE}/cycle-times/weld-rates?weldTypeId=${weldTypeId}&pipeSizeId=${pipeSizeId}`, {
    method: "DELETE",
  });
}

// ── Spool composition ────────────────────────────────────────────
// 404 → no composition recorded yet; the editor treats that as
// "blank form" not a hard error, so the helper normalises by
// catching and returning null.
export async function getSpoolComposition(
  jobId: number,
  spoolId: number,
): Promise<SpoolCompositionDto | null> {
  try {
    return await api<SpoolCompositionDto>(
      `${BASE}/jobs/${jobId}/spools/${spoolId}/composition`,
    );
  } catch (e) {
    if (e instanceof Error && /404/.test(e.message)) return null;
    throw e;
  }
}

export async function upsertSpoolComposition(
  jobId: number,
  spoolId: number,
  input: SpoolCompositionUpsertInputT,
): Promise<SpoolCompositionDto> {
  return api<SpoolCompositionDto>(`${BASE}/jobs/${jobId}/spools/${spoolId}/composition`, {
    method: "PUT",
    json: input,
  });
}

// ── Work calendar ────────────────────────────────────────────────
export function useWorkCalendar(): FetchState<WorkCalendarDto> {
  return useResource(`${BASE}/work-schedule`);
}

export async function updateWorkSchedule(patch: WorkScheduleUpdateInputT): Promise<WorkScheduleDto> {
  return api<WorkScheduleDto>(`${BASE}/work-schedule`, { method: "PATCH", json: patch });
}

export async function createWorkHoliday(input: WorkHolidayCreateInputT): Promise<WorkHolidayDto> {
  return api<WorkHolidayDto>(`${BASE}/work-schedule/holidays`, { method: "POST", json: input });
}

export async function updateWorkHoliday(id: number, patch: WorkHolidayUpdateInputT): Promise<WorkHolidayDto> {
  return api<WorkHolidayDto>(`${BASE}/work-schedule/holidays/${id}`, { method: "PATCH", json: patch });
}

export async function deleteWorkHoliday(id: number): Promise<void> {
  await api(`${BASE}/work-schedule/holidays/${id}`, { method: "DELETE" });
}

export function useCapacity(): FetchState<CapacityResponse> {
  return useResource(`${BASE}/capacity`);
}

export function useResourceLoad(fromIso: string, weeks: number): FetchState<ResourceLoadResponse> {
  return useResource(`${BASE}/capacity/resource-load?from=${fromIso}&weeks=${weeks}`);
}

// ── Mutations ────────────────────────────────────────────────────
export async function createJob(payload: unknown): Promise<JobDto> {
  return api<JobDto>(`${BASE}/jobs`, { method: "POST", json: payload });
}

export async function updateJob(id: number, payload: unknown): Promise<JobDto> {
  return api<JobDto>(`${BASE}/jobs/${id}`, { method: "PATCH", json: payload });
}

export async function deleteJob(id: number): Promise<void> {
  await api(`${BASE}/jobs/${id}`, { method: "DELETE" });
}

export async function replaceSpoolsBulk(
  jobId: number,
  spools: unknown[],
): Promise<void> {
  await api(`${BASE}/jobs/${jobId}/spools`, {
    method: "PATCH",
    json: { spools },
  });
}

// Simple View: replace the job's stage pipeline. Each row is the
// job-stage seed shape (stageKey + status + hours + assignee +
// dates). Persisted on Save, mirroring replaceSpoolsBulk.
export interface JobStageSeed {
  stageKey: string;
  status: SpoolStageDto["status"];
  hours: number;
  assigneeId: number | null;
  startDate: string | null;
  doneDate: string | null;
}

export async function replaceJobStages(
  jobId: number,
  stages: JobStageSeed[],
): Promise<{ stages: SpoolStageDto[] }> {
  return api<{ stages: SpoolStageDto[] }>(`${BASE}/jobs/${jobId}/job-stages`, {
    method: "PUT",
    json: { stages },
  });
}

// Single-spool field patch. Only the fields present are touched, so
// the bulk due-date action can persist just that column without
// pushing the whole (possibly mid-edit) spool list through
// replaceSpoolsBulk.
export async function patchSpool(
  jobId: number,
  spoolId: number,
  patch: { dueDate?: string | null },
): Promise<SpoolDto> {
  return api<SpoolDto>(`${BASE}/jobs/${jobId}/spools/${spoolId}`, {
    method: "PATCH",
    json: patch,
  });
}

// One-shot fetch — used to reconcile auto-promoted spools the
// background extractor created mid-modal-session (the drawings
// poll only returns match rows, not the spool DTOs themselves).
export async function listSpoolsForJob(jobId: number): Promise<SpoolDto[]> {
  const res = await api<{ spools: SpoolDto[] }>(`${BASE}/jobs/${jobId}/spools`);
  return res.spools;
}

export async function importSpools(
  jobId: number,
  rows: unknown[],
): Promise<void> {
  await api(`${BASE}/jobs/${jobId}/spools/import`, {
    method: "POST",
    json: { rows },
  });
}

export async function createTeamMember(payload: unknown): Promise<TeamMemberDto> {
  return api<TeamMemberDto>(`${BASE}/team`, { method: "POST", json: payload });
}

export async function updateTeamMember(
  id: number,
  payload: unknown,
): Promise<TeamMemberDto> {
  return api<TeamMemberDto>(`${BASE}/team/${id}`, {
    method: "PATCH",
    json: payload,
  });
}

export async function deleteTeamMember(id: number): Promise<void> {
  await api(`${BASE}/team/${id}`, { method: "DELETE" });
}

export async function createSolutionType(
  payload: unknown,
): Promise<SolutionTypeDto> {
  return api<SolutionTypeDto>(`${BASE}/solution-types`, {
    method: "POST",
    json: payload,
  });
}

export async function updateSolutionType(
  id: number,
  payload: unknown,
): Promise<SolutionTypeDto> {
  return api<SolutionTypeDto>(`${BASE}/solution-types/${id}`, {
    method: "PATCH",
    json: payload,
  });
}

export async function deleteSolutionType(id: number): Promise<void> {
  await api(`${BASE}/solution-types/${id}`, { method: "DELETE" });
}

// ── Stages + auto-schedule ───────────────────────────────────────
export async function patchSpoolStage(
  jobId: number,
  spoolId: number,
  stageKey: string,
  patch: SpoolStageUpdateInputT,
): Promise<SpoolStageDto> {
  return api<SpoolStageDto>(
    `${BASE}/jobs/${jobId}/spools/${spoolId}/stages/${stageKey}`,
    { method: "PATCH", json: patch },
  );
}

export async function autoScheduleJob(jobId: number, fromDate: string): Promise<{ writes: number }> {
  return api<{ writes: number }>(
    `${BASE}/jobs/${jobId}/auto-schedule`,
    { method: "POST", json: { fromDate } },
  );
}

// Split the job's nominal labour budget evenly across its spools into
// the per-spool stage hours. `nominalStageHours` is sent so the Spools
// tab can distribute figures still sitting unsaved in the Details
// form; omit it to use the map persisted on the job.
export async function distributeNominalHours(
  jobId: number,
  nominalStageHours?: Record<string, number>,
): Promise<NominalDistributionResult> {
  return api<NominalDistributionResult>(
    `${BASE}/jobs/${jobId}/distribute-nominal-hours`,
    { method: "POST", json: nominalStageHours ? { nominalStageHours } : {} },
  );
}

// ── Drawings ─────────────────────────────────────────────────────
export async function listDrawings(jobId: number): Promise<{ drawings: DrawingDto[] }> {
  return api<{ drawings: DrawingDto[] }>(`${BASE}/jobs/${jobId}/drawings`);
}

export async function mintDrawingUploadUrl(
  jobId: number,
  filename: string,
  contentType: string,
): Promise<{ blobPath: string; uploadUrl: string; expiresAt: string }> {
  return api(`${BASE}/jobs/${jobId}/drawings/upload-url`, {
    method: "POST",
    json: { filename, contentType },
  });
}

export async function registerDrawing(
  jobId: number,
  payload: { filename: string; blobPath: string; sizeBytes?: number },
): Promise<DrawingDto> {
  return api<DrawingDto>(`${BASE}/jobs/${jobId}/drawings`, {
    method: "POST",
    json: payload,
  });
}

export async function deleteDrawing(jobId: number, drawingId: number): Promise<void> {
  await api(`${BASE}/jobs/${jobId}/drawings/${drawingId}`, { method: "DELETE" });
}

export async function getDrawingPreviewUrl(
  jobId: number,
  drawingId: number,
): Promise<{ url: string; expiresAt: string }> {
  return api(`${BASE}/jobs/${jobId}/drawings/${drawingId}/preview-url`);
}

export async function addDrawingManualMatch(
  jobId: number,
  drawingId: number,
  spoolId: number,
  pageNumber: number | null,
): Promise<void> {
  await api(`${BASE}/jobs/${jobId}/drawings/${drawingId}/matches`, {
    method: "POST",
    json: { spoolId, pageNumber },
  });
}

export async function setDrawingMatchConfirmed(
  jobId: number,
  drawingId: number,
  matchId: number,
  confirmed: boolean,
): Promise<void> {
  await api(`${BASE}/jobs/${jobId}/drawings/${drawingId}/matches/${matchId}`, {
    method: "PATCH",
    json: { confirmed },
  });
}

export async function deleteDrawingMatch(
  jobId: number,
  drawingId: number,
  matchId: number,
): Promise<void> {
  await api(`${BASE}/jobs/${jobId}/drawings/${drawingId}/matches/${matchId}`, {
    method: "DELETE",
  });
}

export async function promoteDrawingCandidate(
  jobId: number,
  drawingId: number,
  candidateId: number,
): Promise<{ spool: SpoolDto; match: DrawingSpoolMatchDto }> {
  return api<{ spool: SpoolDto; match: DrawingSpoolMatchDto }>(
    `${BASE}/jobs/${jobId}/drawings/${drawingId}/candidates/${candidateId}/promote`,
    { method: "POST" },
  );
}

export async function dismissDrawingCandidate(
  jobId: number,
  drawingId: number,
  candidateId: number,
): Promise<void> {
  await api(`${BASE}/jobs/${jobId}/drawings/${drawingId}/candidates/${candidateId}`, {
    method: "DELETE",
  });
}

// Ensure a spool exists for the given drawing and link it. `num`
// override is rarely needed — server falls back to the filename ref
// when omitted.
export async function ensureSpoolFromDrawing(
  jobId: number,
  drawingId: number,
  num?: string,
): Promise<{ spool: SpoolDto; match: DrawingSpoolMatchDto; created: boolean }> {
  return api<{ spool: SpoolDto; match: DrawingSpoolMatchDto; created: boolean }>(
    `${BASE}/jobs/${jobId}/drawings/${drawingId}/ensure-spool`,
    { method: "POST", json: num ? { num } : {} },
  );
}

// ── Messages ─────────────────────────────────────────────────────
export async function listMessages(jobId: number): Promise<{ messages: JobMessageDto[] }> {
  return api<{ messages: JobMessageDto[] }>(`${BASE}/jobs/${jobId}/messages`);
}

export async function postMessage(
  jobId: number,
  body: string,
  mentions: string[] = [],
): Promise<JobMessageDto> {
  return api<JobMessageDto>(`${BASE}/jobs/${jobId}/messages`, {
    method: "POST",
    json: { body, mentions },
  });
}

export async function deleteMessage(jobId: number, messageId: number): Promise<void> {
  await api(`${BASE}/jobs/${jobId}/messages/${messageId}`, { method: "DELETE" });
}

// @mention picker — searches active planner_users + (if Graph
// configured) the tenant directory. PickerRow.id is the planner_user
// uuid when pendingProjection=false, the Entra object id otherwise;
// the editor calls projectAppUser before inserting the mention so
// the persisted id is always the planner_user uuid.
export interface AppUserPickerRow {
  id: string;
  displayName: string | null;
  email: string | null;
  entraObjectId: string;
  pendingProjection: boolean;
}

export async function searchAppUsers(q: string): Promise<AppUserPickerRow[]> {
  const search = q ? `?q=${encodeURIComponent(q)}` : "";
  const out = await api<{ users: AppUserPickerRow[] }>(`${BASE}/users${search}`);
  return out.users;
}

export async function projectAppUser(row: AppUserPickerRow): Promise<AppUserPickerRow> {
  const projected = await api<{
    id: string;
    entraObjectId: string;
    displayName: string | null;
    email: string | null;
  }>(`${BASE}/users/projection`, {
    method: "POST",
    json: {
      entraObjectId: row.entraObjectId,
      displayName: row.displayName,
      email: row.email,
    },
  });
  return { ...projected, pendingProjection: false };
}
